import { XMarkIcon } from "@heroicons/react/24/outline";
import { ActionType, DataExportId, DataExportStatus, RegionId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import {
    dataExportRefetchInterval,
    downloadDataExportFile,
} from "components/exports/data-export-status";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React from "react";
import { Link } from "react-router-dom";
import { useIsAllowed } from "store/selectors/hooks";
import {
    ActivitiesReportColumnFilter,
    ActivitiesReportFilters,
    organizationTypesForQuery,
} from "./activities-report";

interface ActivitiesExportScope {
    regionId: RegionId;
    startDate: string;
    endDate: string;
    filters: ActivitiesReportFilters;
    columnFilter: ActivitiesReportColumnFilter;
}

export const useActivitiesExport = () => {
    const trpcClient = trpc.useTRPC();
    return useMutation(trpcClient.activitiesReport.requestExport.mutationOptions());
};

export type ActivitiesExportRequest = ReturnType<typeof useActivitiesExport>;

interface ExportActivitiesButtonProps {
    exportRequest: ActivitiesExportRequest;
    scope: ActivitiesExportScope;
}

export const ExportActivitiesButton: React.FC<ExportActivitiesButtonProps> = props => {
    const canExport = useIsAllowed(ActionType.ExportCsv);
    const { exportRequest, scope } = props;

    if (!canExport) {
        return null;
    }

    const startExport = () => {
        if (exportRequest.isPending) {
            return;
        }
        exportRequest.mutate({
            regionId: scope.regionId,
            startDate: scope.startDate,
            endDate: scope.endDate,
            recordTypes: scope.filters.recordTypes,
            organizationTypes: organizationTypesForQuery(scope.filters.organizationTypes),
            staffUserIds: scope.filters.staffUserIds,
            columnFilter: scope.columnFilter,
        });
    };

    return (
        <SecondaryCompactButton onClick={startExport}>
            Export activities
        </SecondaryCompactButton>
    );
};

const DismissButton: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => (
    <SecondaryCompactButton
        aria-label="Dismiss"
        className="-my-1.5 ml-4 shrink-0"
        onClick={onDismiss}>
        <XMarkIcon className="h-5 w-5" />
    </SecondaryCompactButton>
);

interface ExportBannerProps {
    type: "success" | "error" | "info";
    onDismiss: () => void;
    children: React.ReactNode;
}

const ExportBanner: React.FC<ExportBannerProps> = props => (
    <InlineBanner type={props.type}>
        <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {props.children}
            </div>
            <DismissButton onDismiss={props.onDismiss} />
        </div>
    </InlineBanner>
);

const ExportDescription: React.FC<{ description: string | null | undefined }> = ({
    description,
}) => (description ? <span className="font-medium">{description}</span> : null);

const ViewExportLink: React.FC<{ exportId: DataExportId }> = ({ exportId }) => (
    <Link to={`/exports/${exportId}`} className={defaultLinkStyle}>
        View export
    </Link>
);

type RequestedExport = NonNullable<
    inferRouterOutputs<AppRouter>["dataExport"]["getById"]
>;

interface ExportStatusBannerProps {
    exportId: DataExportId;
    onDismiss: () => void;
}

const UncheckedExportBanner: React.FC<ExportStatusBannerProps> = props => (
    <ExportBanner type="error" onDismiss={props.onDismiss}>
        <span>
            We couldn&apos;t check on your export. You&apos;ll still get an email when
            it&apos;s ready.
        </span>
        <ViewExportLink exportId={props.exportId} />
    </ExportBanner>
);

const ReadyExportBanner: React.FC<{
    dataExport: RequestedExport;
    fileId: string;
    onDismiss: () => void;
}> = props => (
    <ExportBanner type="success" onDismiss={props.onDismiss}>
        <span>Your export is ready.</span>
        <ExportDescription description={props.dataExport.description} />
        <SecondaryCompactButton
            className="-my-1"
            onClick={() => downloadDataExportFile(props.fileId)}>
            Download
        </SecondaryCompactButton>
    </ExportBanner>
);

const FailedExportBanner: React.FC<{
    dataExport: RequestedExport;
    onDismiss: () => void;
}> = props => (
    <ExportBanner type="error" onDismiss={props.onDismiss}>
        <span>Your export failed. Please try again.</span>
        <ExportDescription description={props.dataExport.description} />
        <ViewExportLink exportId={props.dataExport.id} />
    </ExportBanner>
);

const PreparingExportBanner: React.FC<
    ExportStatusBannerProps & { dataExport: RequestedExport | undefined }
> = props => (
    <ExportBanner type="info" onDismiss={props.onDismiss}>
        <span>Preparing your export. We&apos;ll email you when it&apos;s ready.</span>
        <ExportDescription description={props.dataExport?.description} />
        <ViewExportLink exportId={props.exportId} />
    </ExportBanner>
);

const RequestedExportBanner: React.FC<ExportStatusBannerProps> = ({
    exportId,
    onDismiss,
}) => {
    const trpcClient = trpc.useTRPC();
    const statusQuery = useQuery({
        ...trpcClient.dataExport.getById.queryOptions({ id: exportId }),
        refetchInterval: query =>
            dataExportRefetchInterval(query.state.data ? [query.state.data.status] : []),
    });
    const dataExport = statusQuery.data;
    if (statusQuery.isError || dataExport === null) {
        return <UncheckedExportBanner exportId={exportId} onDismiss={onDismiss} />;
    }
    if (dataExport?.status === DataExportStatus.Completed && dataExport.fileId) {
        return (
            <ReadyExportBanner
                dataExport={dataExport}
                fileId={dataExport.fileId}
                onDismiss={onDismiss}
            />
        );
    }
    if (dataExport?.status === DataExportStatus.Failed) {
        return <FailedExportBanner dataExport={dataExport} onDismiss={onDismiss} />;
    }
    return (
        <PreparingExportBanner
            exportId={exportId}
            dataExport={dataExport}
            onDismiss={onDismiss}
        />
    );
};

export const ActivitiesExportBanner: React.FC<{
    exportRequest: ActivitiesExportRequest;
}> = ({ exportRequest }) => {
    if (exportRequest.isError) {
        return (
            <ExportBanner type="error" onDismiss={exportRequest.reset}>
                <span>The export could not be started. Please try again.</span>
            </ExportBanner>
        );
    }
    if (exportRequest.isSuccess) {
        return (
            <RequestedExportBanner
                exportId={exportRequest.data.id}
                onDismiss={exportRequest.reset}
            />
        );
    }
    return null;
};
